import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token and selected org
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const selectedOrg = useOrganizationStore.getState().selectedOrg;
    if (selectedOrg?._id) {
      config.headers['X-Org-Id'] = selectedOrg._id;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Refresh "single-flight": un único refresh en curso compartido por todas las
// peticiones que caduquen a la vez. Sin esto, N peticiones concurrentes con 401
// lanzaban N refresh; si el backend rota el refresh token, todos menos el
// primero fallaban → logout intermitente (p.ej. durante el polling de una
// transcripción larga en Reuniones).
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) throw new Error('No refresh token');
  const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
  const { tokens } = response.data;
  useAuthStore.getState().setTokens(tokens);
  return tokens.accessToken as string;
}

// Response interceptor - handle 401 and refresh token
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 401 and not already retrying (y no es el propio /auth/refresh)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      originalRequest._retry = true;

      try {
        // Comparte un único refresh entre todas las peticiones concurrentes.
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }
        const newAccessToken = await refreshPromise;

        // Retry original request con el token nuevo
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch {
        // Refresh falló (o no hay refresh token) → logout
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  register: (email: string, password: string, firstName?: string, lastName?: string) =>
    api.post('/auth/register', { email, password, first_name: firstName, last_name: lastName }),

  logout: () =>
    api.post('/auth/logout'),

  me: () =>
    api.get('/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),

  checkInvitation: (token: string) =>
    api.get(`/users/invitation/status?token=${token}`),

  acceptInvitation: (token: string, password: string) =>
    api.post('/users/accept-invitation', { token, password }),

  // 2FA
  verify2fa: (preAuthToken: string, code: string) =>
    api.post('/auth/2fa/verify', { pre_auth_token: preAuthToken, code }),

  get2faStatus: () =>
    api.get('/auth/2fa/status'),

  setup2fa: () =>
    api.post('/auth/2fa/setup'),

  enable2fa: (totpCode: string) =>
    api.post('/auth/2fa/enable', { totp_code: totpCode }),

  disable2fa: (password: string) =>
    api.post('/auth/2fa/disable', { password }),

  regenerateBackupCodes: (totpCode: string) =>
    api.post('/auth/2fa/regenerate-backup-codes', { totp_code: totpCode }),
};

// Organizations API
export const organizationsApi = {
  list: (page = 1, limit = 20, status?: string) =>
    api.get('/organizations', { params: { page, limit, status } }),

  my: () =>
    api.get('/organizations/my'),

  hasEcommerce: () =>
    api.get('/organizations/has-ecommerce'),

  hasCatalog: () =>
    api.get('/organizations/has-catalog'),

  get: (id: string) =>
    api.get(`/organizations/${id}`),

  create: (data: { name: string; slug: string; config?: object }) =>
    api.post('/organizations', data),

  update: (id: string, data: object) =>
    api.patch(`/organizations/${id}`, data),

  delete: (id: string) =>
    api.delete(`/organizations/${id}`),

  suspend: (id: string) =>
    api.post(`/organizations/${id}/suspend`),

  addMember: (orgId: string, userId: string, roleId: string) =>
    api.post(`/organizations/${orgId}/members`, { userId, roleId }),

  // Invite a user by email. Backend resolves whether the email is brand new
  // (full invitation flow), already pending (resend with new role) or active
  // (direct role assignment + notification email).
  invite: (
    orgId: string,
    data: { email: string; roleId: string; firstName?: string; lastName?: string }
  ) => api.post(`/organizations/${orgId}/invite`, data),

  getMembers: (orgId: string, page = 1, limit = 20) =>
    api.get(`/organizations/${orgId}/members`, { params: { page, limit } }),

  removeMember: (orgId: string, userId: string) =>
    api.delete(`/organizations/${orgId}/members/${userId}`),

  getEcommerceDb: (orgId: string) =>
    api.get(`/organizations/${orgId}/ecommerce-db`),

  createEcommerceDb: (orgId: string) =>
    api.post(`/organizations/${orgId}/ecommerce-db`),

  resetEcommerceDb: (orgId: string, confirm: boolean) =>
    api.post(`/organizations/${orgId}/ecommerce-db/reset`, { confirm }),

  initialize: (orgId: string) =>
    api.post(`/organizations/${orgId}/initialize`),

  getDeploymentInfo: (orgId: string) =>
    api.get(`/organizations/${orgId}/deployment-info`),

  syncCustomers: (orgId: string) =>
    api.post(`/customers-sync/sync/${orgId}`),

  getCustomersSyncStatus: (orgId: string) =>
    api.get(`/customers-sync/status/${orgId}`),

  getPaymentConfig: (orgId: string) =>
    api.get(`/organizations/${orgId}/payment-config`),

  savePaymentConfig: (orgId: string, data: object) =>
    api.put(`/organizations/${orgId}/payment-config`, data),
  // Da de alta / actualiza en Stripe el webhook de la tienda con todos sus eventos.
  setupStripeWebhook: (orgId: string) =>
    api.post<{ data: { status: 'created' | 'recreated' | 'updated'; url: string; events: string[] } }>(
      `/organizations/${orgId}/payment-config/stripe-webhook`
    ),

  getTrackingConfig: (orgId: string) =>
    api.get(`/organizations/${orgId}/tracking-config`),

  saveTrackingConfig: (
    orgId: string,
    data: { ga4_id?: string; meta_pixel_id?: string; gtm_id?: string }
  ) => api.put(`/organizations/${orgId}/tracking-config`, data),

  getNotificationConfig: (orgId: string) =>
    api.get(`/organizations/${orgId}/notification-config`),

  saveNotificationConfig: (
    orgId: string,
    data: {
      email?: { enabled?: boolean; minLevel?: 'info' | 'warning' | 'critical' };
      telegram?: { enabled?: boolean; minLevel?: 'info' | 'warning' | 'critical'; chatId?: string; botToken?: string };
      whatsapp?: { enabled?: boolean; minLevel?: 'info' | 'warning' | 'critical'; phoneNumberId?: string; accessToken?: string; recipients?: string[]; useTemplate?: boolean; templateName?: string; templateLang?: string };
      webpublish?: { enabled?: boolean };
    }
  ) => api.put(`/organizations/${orgId}/notification-config`, data),

  // gk2control integration (presence/clock-in API). See backend organization.routes.
  getGk2ControlConfig: (orgId: string) =>
    api.get(`/organizations/${orgId}/gk2control-config`),
  saveGk2ControlConfig: (
    orgId: string,
    data: { enabled?: boolean; companyNif?: string; apiBaseUrl?: string; apiToken?: string; acceptUnsignedWebhook?: boolean; autoSalidaEnabled?: boolean; autoSalidaGraceMin?: number; roundingEnabled?: boolean; roundingIntervalMin?: number; roundingMarginMin?: number }
  ) => api.put(`/organizations/${orgId}/gk2control-config`, data),
  regenerateGk2WebhookSecret: (orgId: string) =>
    api.post(`/organizations/${orgId}/gk2control-config/regenerate-webhook-secret`),
  testGk2ControlConnection: (orgId: string, data?: { apiToken?: string; apiBaseUrl?: string }) =>
    api.post(`/organizations/${orgId}/gk2control-config/test`, data || {}),
};

// Databases API
export const databasesApi = {
  list: (page = 1, limit = 20) =>
    api.get('/databases', { params: { page, limit } }),

  stats: (orgId: string) =>
    api.get(`/databases/${orgId}/stats`),

  backup: (orgId: string) =>
    api.post(`/databases/${orgId}/backup`),

  listBackups: (orgId: string) =>
    api.get(`/databases/${orgId}/backups`),

  restore: (orgId: string, backupName: string) =>
    api.post(`/databases/${orgId}/restore`, { backupName }),

  downloadBackup: (orgId: string, backupName: string) =>
    api.get(`/databases/${orgId}/backups/${backupName}/download`, {
      responseType: 'blob',
    }),
};

// Users API
export const usersApi = {
  list: (page = 1, limit = 20) =>
    api.get('/users', { params: { page, limit } }),

  get: (id: string) =>
    api.get(`/users/${id}`),

  // organization_id (VIDA-293): si se envía, los roles se asignan acotados a esa
  // organización en vez de como roles globales (usuario "suelto").
  create: (data: { email: string; first_name?: string; last_name?: string; roles?: string[]; user_type?: 'internal' | 'external'; organization_id?: string }) =>
    api.post('/users', data),

  update: (id: string, data: object) =>
    api.patch(`/users/${id}`, data),

  delete: (id: string) =>
    api.delete(`/users/${id}`),

  getRoles: (id: string) =>
    api.get(`/users/${id}/roles`),

  assignRole: (userId: string, roleId: string, orgId?: string) =>
    api.post(`/users/${userId}/roles`, { roleId, orgId }),

  removeRole: (userId: string, roleId: string, orgId?: string) =>
    api.delete(`/users/${userId}/roles/${roleId}`, { params: { orgId } }),

  // Fuerza el envío del email de recuperación (super admin). A diferencia del
  // endpoint público, este SÍ devuelve el error real si el correo no sale.
  sendPasswordReset: (userId: string) =>
    api.post(`/users/${userId}/send-password-reset`),

  resendInvitation: (userId: string) =>
    api.post(`/users/invitation/resend/${userId}`),

  cancelInvitation: (userId: string) =>
    api.post(`/users/invitation/cancel/${userId}`),

  getOrganizations: (userId: string) =>
    api.get(`/users/${userId}/organizations`),

  forceDisable2fa: (userId: string) =>
    api.post(`/users/${userId}/2fa/force-disable`),
};

// Roles API
export const rolesApi = {
  list: (orgId?: string) =>
    api.get('/roles', { params: { orgId } }),

  get: (id: string) =>
    api.get(`/roles/${id}`),

  create: (data: { name: string; permissions: string[]; orgId?: string; description?: string }) =>
    api.post('/roles', data),

  update: (id: string, data: object) =>
    api.patch(`/roles/${id}`, data),

  delete: (id: string) =>
    api.delete(`/roles/${id}`),

  seedDemo: () =>
    api.post('/roles/seed-demo'),

  seedPresenceSupervisor: () =>
    api.post('/roles/seed-presence-supervisor'),
};

// Server metrics API
export const metricsApi = {
  server: () =>
    api.get('/metrics/server'),

  services: () =>
    api.get('/metrics/services'),

  history: (metric: string, hours = 24) =>
    api.get(`/metrics/history/${metric}`, { params: { hours } }),
};

// System API
export const systemApi = {
  restartServices: () =>
    api.post('/system/restart-services'),

  clearCache: () =>
    api.post('/system/clear-cache'),

  getLogs: (lines = 100, service?: string) =>
    api.get('/system/logs', { params: { lines, service } }),

  getDockerStatus: () =>
    api.get('/system/docker-status'),

  // Espacio en disco del servidor + comandos de limpieza (VIDA-309)
  getDisk: () =>
    api.get('/system/disk'),

  runDiskCheck: () =>
    api.post('/system/disk/check'),

  cleanupLogs: () =>
    api.post('/system/cleanup-logs'),
};

// System Backups API
export const backupsApi = {
  list: (page = 1, limit = 20) =>
    api.get('/backups', { params: { page, limit } }),

  stats: () =>
    api.get('/backups/stats'),

  create: (type: 'full' | 'mongodb') =>
    api.post('/backups', { type }),

  download: async (filename: string) => {
    const response = await api.get(`/backups/${filename}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  delete: (filename: string) =>
    api.delete(`/backups/${filename}`),
};

// Audit logs API
export const auditApi = {
  list: (page = 1, limit = 50, filters?: { action?: string; actorId?: string; orgId?: string }) =>
    api.get('/audit', { params: { page, limit, ...filters } }),

  get: (id: string) =>
    api.get(`/audit/${id}`),
};

// Storage API (MinIO)
export const storageApi = {
  stats: () =>
    api.get('/storage/stats'),

  listBuckets: () =>
    api.get('/storage/buckets'),

  listObjects: (bucketName: string, prefix?: string, recursive?: boolean, page = 1, limit = 20) =>
    api.get(`/storage/buckets/${bucketName}/objects`, {
      params: { prefix, recursive, page, limit },
    }),

  getUploadUrl: (bucketName: string, objectName: string) =>
    api.post(`/storage/buckets/${bucketName}/upload-url`, { objectName }),

  deleteObject: (bucketName: string, objectName: string) =>
    api.delete(`/storage/buckets/${bucketName}/objects/${objectName}`),

  health: () =>
    api.get('/storage/health'),

  // For non-super_admin users — returns the bucket of the currently selected
  // organization (X-Org-Id header is sent by the axios interceptor).
  myBucket: () =>
    api.get('/storage/my-bucket'),
};

// Cupones de la tienda (VIDA-350)
export type CouponType = 'percent' | 'fixed';
export interface Coupon {
  _id: string;
  code: string;
  description?: string;
  type: CouponType;
  value: number;
  min_subtotal: number;
  starts_at?: string | null;
  ends_at?: string | null;
  max_uses?: number | null;
  max_uses_per_customer?: number | null;
  stackable: boolean;
  active: boolean;
  usage?: { redeemed: number; reserved: number; discount: number };
  created_at: string;
}
export type CouponInput = Partial<Omit<Coupon, '_id' | 'usage' | 'created_at'>>;
export interface CouponRedemption {
  _id: string;
  order_number?: string;
  email: string;
  discount: number;
  status: 'reserved' | 'redeemed' | 'released';
  reserved_until: string;
  created_at: string;
}

export const couponsApi = {
  list: () => api.get<{ coupons: Coupon[] }>('/ecommerce/coupons'),
  create: (data: CouponInput) => api.post<{ coupon: Coupon }>('/ecommerce/coupons', data),
  update: (id: string, data: CouponInput) => api.patch<{ coupon: Coupon }>(`/ecommerce/coupons/${id}`, data),
  remove: (id: string) => api.delete(`/ecommerce/coupons/${id}`),
  redemptions: (id: string) => api.get<{ redemptions: CouponRedemption[] }>(`/ecommerce/coupons/${id}/redemptions`),
};

// Ecommerce API
export const ecommerceApi = {
  stats: () =>
    api.get('/ecommerce/stats'),

  getNoSalesProducts: (page = 1, limit = 10) =>
    api.get('/ecommerce/stats/no-sales', { params: { page, limit } }),

  getProducts: (page = 1, limit = 20, filters?: { status?: string; category?: string; search?: string }) =>
    api.get('/ecommerce/products', { params: { page, limit, ...filters } }),

  getProduct: (id: string) =>
    api.get(`/ecommerce/products/${id}`),

  // Editor de productos de ecommerce (VIDA-292): edita ecommerce_meta (override),
  // NO toca el catálogo. "Quitar" = updateProduct(id, { ecommerce_active: false }).
  createProduct: (data: Record<string, unknown>) =>
    api.post('/ecommerce/products', data),

  updateProduct: (id: string, data: Record<string, unknown>) =>
    api.patch(`/ecommerce/products/${id}`, data),

  getOrders: (page = 1, limit = 20, filters?: { status?: string; startDate?: string; endDate?: string }) =>
    api.get('/ecommerce/orders', { params: { page, limit, ...filters } }),

  getOrder: (id: string) =>
    api.get(`/ecommerce/orders/${id}`),

  updateOrderStatus: (id: string, status: string, notes?: string) =>
    api.patch(`/ecommerce/orders/${id}/status`, { status, notes }),

  getCustomers: (page = 1, limit = 20, filters?: { search?: string; hasOrders?: boolean }) =>
    api.get('/ecommerce/customers', { params: { page, limit, ...filters } }),

  getCustomer: (id: string) =>
    api.get(`/ecommerce/customers/${id}`),

  updateCustomer: (id: string, data: { first_name?: string; last_name?: string; phone?: string; notes?: string; tags?: string[] }) =>
    api.patch(`/ecommerce/customers/${id}`, data),

  createCustomer: (data: { email: string; first_name?: string; last_name?: string; phone?: string; notes?: string; tags?: string[] }) =>
    api.post('/ecommerce/customers', data),

  deleteCustomer: (id: string) =>
    api.delete(`/ecommerce/customers/${id}`),
};

// Catalog API. `catalog` = id del catálogo adicional (VIDA-370); sin él, el principal.
const catalogParam = (catalog?: string | null) => (catalog ? { catalog } : {});

export interface CatalogSummary {
  _id: string | null;
  name: string;
  slug: string;
  is_primary: boolean;
  type: string;
  objectives: string[];
  storytelling: string;
  status: 'draft' | 'active';
}

export interface CatalogType {
  id: string;
  code: string;
  name: string;
}

export const catalogApi = {
  getProducts: (page = 1, limit = 20, filters?: { category?: string; status?: string; search?: string; featured?: boolean }, catalog?: string | null) =>
    api.get('/catalog/products', { params: { page, limit, ...filters, ...catalogParam(catalog) } }),

  getProduct: (id: string, catalog?: string | null) =>
    api.get(`/catalog/products/${id}`, { params: catalogParam(catalog) }),

  updateProduct: (id: string, data: object, catalog?: string | null) =>
    api.patch(`/catalog/products/${id}`, data, { params: catalogParam(catalog) }),

  getCategories: (catalog?: string | null) =>
    api.get('/catalog/categories', { params: catalogParam(catalog) }),

  createProduct: (data: object, catalog?: string | null) =>
    api.post('/catalog/products', data, { params: catalogParam(catalog) }),

  deleteProduct: (id: string) =>
    api.delete(`/catalog/products/${id}`),

  stats: (catalog?: string | null) =>
    api.get('/catalog/stats', { params: catalogParam(catalog) }),

  listCatalogs: () =>
    api.get<{ catalogs: CatalogSummary[] }>('/catalog/catalogs'),

  catalogTypes: () =>
    api.get<{ types: CatalogType[] }>('/catalog/catalog-types'),

  createCatalog: (data: { name: string; type?: string; objectives: string[]; storytelling?: string; status?: 'draft' | 'active' }) =>
    api.post<{ catalog: CatalogSummary }>('/catalog/catalogs', data),

  updateCatalog: (ref: string, data: Partial<Pick<CatalogSummary, 'name' | 'type' | 'objectives' | 'storytelling' | 'status'>>) =>
    api.patch<{ catalog: CatalogSummary }>(`/catalog/catalogs/${ref}`, data),

  deleteCatalog: (ref: string) =>
    api.delete(`/catalog/catalogs/${ref}`),

  setCatalogProducts: (ref: string, changes: { add?: string[]; remove?: string[] }) =>
    api.post<{ added: number; removed: number }>(`/catalog/catalogs/${ref}/products`, changes),
};

// Blog API (editorial posts per organization)
export const blogApi = {
  getPosts: (
    page = 1,
    limit = 20,
    filters?: { status?: string; tag?: string; search?: string; locale?: string }
  ) => api.get('/blog/posts', { params: { page, limit, ...filters } }),

  getPost: (id: string) => api.get(`/blog/posts/${id}`),

  createPost: (data: object) => api.post('/blog/posts', data),

  updatePost: (id: string, data: object) => api.patch(`/blog/posts/${id}`, data),

  deletePost: (id: string) => api.delete(`/blog/posts/${id}`),

  publishPost: (id: string) => api.post(`/blog/posts/${id}/publish`),

  unpublishPost: (id: string) => api.post(`/blog/posts/${id}/unpublish`),

  getTags: () => api.get('/blog/tags'),

  // Returns { uploadUrl, objectKey, publicUrl }. The browser does the PUT
  // directly to MinIO with the presigned URL.
  getUploadUrl: (filename: string) => api.post('/blog/upload-url', { filename }),
};

// AI assistant API (Fase F) — local SEO/SEM/Marketing generation
export type AiTask =
  | 'meta-description'
  | 'keywords'
  | 'alt-text'
  | 'social-post'
  | 'product-description'
  | 'marketing-pack';

export const aiApi = {
  usage: () => api.get('/ai/usage'),

  generate: (task: AiTask, context: string, language: 'es' | 'en' = 'es') =>
    api.post(`/ai/generate/${task}`, { context, language }),
};

// Marketing API (UTM builder history + campaign report). Org-scoped via the
// X-Org-Id header added by the request interceptor.
export interface UtmLink {
  _id: string;
  organizationId: string;
  name: string;
  base_url: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content?: string;
  utm_term?: string;
  full_url: string;
  createdAt: string;
}

export interface CampaignReport {
  hasEcommerce: boolean;
  range: { startDate: string; endDate: string };
  campaigns: { campaign: string; visits: number; orders: number; revenue: number; source?: string; medium?: string }[];
  channels: { source: string; medium: string; visits: number }[];
  totals: { visits: number; orders: number; revenue: number };
}

export const marketingApi = {
  getUtmLinks: () => api.get('/marketing/utm-links'),

  createUtmLink: (data: Omit<UtmLink, '_id' | 'organizationId' | 'createdAt'>) =>
    api.post('/marketing/utm-links', data),

  deleteUtmLink: (id: string) => api.delete(`/marketing/utm-links/${id}`),

  getCampaignReport: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/marketing/campaigns/report', { params }),

  // Announcements (Module C, web-publish): public notifications for the org site.
  getAnnouncements: () => api.get('/marketing/announcements'),
  createAnnouncement: (data: { title: string; body?: string; level?: 'info' | 'warning' | 'critical'; scheduled_for?: string | null; expiresAt?: string | null; audience?: 'org' | 'roles' | 'users'; roleIds?: string[]; userIds?: string[]; email?: boolean }) =>
    api.post('/marketing/announcements', data),
  deleteAnnouncement: (id: string) => api.delete(`/marketing/announcements/${id}`),
};

export interface AppAnnouncement {
  _id: string;
  title: string;
  body?: string;
  level: 'info' | 'warning' | 'critical';
  scheduled_for?: string | null;
  expiresAt?: string | null;
  publicWeb?: boolean;
  roleIds?: string[];
  userIds?: string[];
  channels?: string[];
  createdAt: string;
}

// Planner API (Module A). Org-scoped via the X-Org-Id header.
export interface PlannerTaxNode { _id: string; id: string; name: string; parent_id: string | null; depth: number }
export interface PlannerTaxTree { _id: string; id: string; name: string; nodes: PlannerTaxNode[] }
export interface PlannerTaxonomies { domain: { id: string; name: string } | null; trees: PlannerTaxTree[] }

export interface PlannerResource {
  _id: string;
  organization_id: string;
  name: string;
  parent_id?: string | null;
  typeNodeId?: string | null;
  businessAreaNodeId?: string | null;
  currentStateNodeId?: string | null;
  groupNodeIds: string[];
  capacity: number;
  exclusive: boolean;
  active: boolean;
  status: 'alta' | 'baja';
  userId?: string | null;
  employeeNif?: string | null;
  scheduleId?: string | null;
  attributes: Record<string, unknown>;
}

// Horario oficial recurrente (A1)
export interface ScheduleSegment { plannedIn: string; forcedOut: string; accountableIn?: string }
export interface ScheduleDay { weekday: number; segments: ScheduleSegment[] }
export interface PlannerSchedule {
  _id: string;
  organization_id: string;
  name: string;
  resourceId?: string | null;
  timezone: string;
  active: boolean;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  days: ScheduleDay[];
  rounding?: { intervalMin: number; marginMin: number } | null;
  attributes: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}
export interface TimesheetDay {
  date: string;
  rawIn: string | null;
  rawOut: string | null;
  accountableIn: string | null;
  accountableOut: string | null;
  outSource: string | null;
  accountableMinutes: number | null;
}
export interface TimesheetResponse {
  nif: string;
  timezone: string;
  rounding: boolean;
  hasSchedule: boolean;
  days: TimesheetDay[];
}

// Proyectos de asignación + disponibilidad (A2)
export interface AllocationProject {
  _id: string;
  organization_id: string;
  name: string;
  code?: string;
  businessAreaNodeId?: string | null;
  status: 'active' | 'inactive' | 'archived';
  color?: string;
  startDate?: string | null;
  endDate?: string | null;
  notes?: string;
  created_at?: string;
}
export interface ResourceAvailability {
  _id: string;
  organization_id: string;
  resourceId: string;
  projectId?: string | null;
  dateFrom: string;
  dateTo: string;
  notes?: string;
  created_at?: string;
}
export interface AllocationReportRow {
  projectId: string | null;
  name: string;
  color?: string;
  minutes: number;
  hours: number;
  days: number;
}
export interface AllocationReport {
  from: string;
  to: string;
  rounding: boolean;
  report: AllocationReportRow[];
}

export interface PlannerService {
  _id: string;
  organization_id: string;
  name: string;
  categoryNodeId?: string | null;
  duration: number;
  price?: number | null;
  currency: string;
  chainable: boolean;
  nextServiceId?: string | null;
  clientTypeNodeId?: string | null;
  active: boolean;
  attributes: Record<string, unknown>;
}

export interface PlannerActivity {
  _id: string;
  organization_id: string;
  name: string;
  format: 'calendar' | 'agenda' | 'list' | 'cmi';
  description?: string;
  active: boolean;
}

export interface PlannerScheduledItem {
  _id: string;
  organization_id: string;
  activityId?: string | null;
  serviceId?: string | null;
  resourceId: string;
  clientId?: string | null;
  clientLabel?: string;
  workerResourceId?: string | null;
  workerUserId?: string | null;
  start: string;
  end: string;
  alarmAt?: string | null;
  stateNodeId?: string | null;
  notes?: string;
  /** 'presence' = planned employee shift; 'work' = service/task; 'absence' = day(s) off. Default 'work'. */
  kind?: 'work' | 'presence' | 'absence';
  absenceKind?: 'vacations' | 'personal' | 'sick' | 'permission' | 'other';
  status?: 'approved' | 'pending' | 'rejected';
  createdBy?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  rejectedReason?: string;
  attributes: Record<string, unknown>;
}

export const plannerApi = {
  getTaxonomies: () => api.get('/planner/taxonomies'),

  getResources: (params?: { parentId?: string; typeNodeId?: string }) =>
    api.get('/planner/resources', { params }),
  createResource: (data: Partial<PlannerResource>) => api.post('/planner/resources', data),
  updateResource: (id: string, data: Partial<PlannerResource>) => api.patch(`/planner/resources/${id}`, data),
  deleteResource: (id: string) => api.delete(`/planner/resources/${id}`),

  getServices: (params?: { categoryNodeId?: string; active?: boolean }) =>
    api.get('/planner/services', { params }),
  createService: (data: Partial<PlannerService>) => api.post('/planner/services', data),
  updateService: (id: string, data: Partial<PlannerService>) => api.patch(`/planner/services/${id}`, data),
  deleteService: (id: string) => api.delete(`/planner/services/${id}`),

  getActivities: () => api.get('/planner/activities'),
  createActivity: (data: Partial<PlannerActivity>) => api.post('/planner/activities', data),
  updateActivity: (id: string, data: Partial<PlannerActivity>) => api.patch(`/planner/activities/${id}`, data),
  deleteActivity: (id: string) => api.delete(`/planner/activities/${id}`),

  getScheduled: (params?: { from?: string; to?: string; resourceId?: string; activityId?: string; kind?: 'work' | 'presence' | 'absence' }) =>
    api.get('/planner/scheduled', { params }),
  createScheduled: (data: Partial<PlannerScheduledItem>) => api.post('/planner/scheduled', data),
  createScheduledBulk: (items: Partial<PlannerScheduledItem>[]) => api.post('/planner/scheduled/bulk', { items }),
  updateScheduled: (id: string, data: Partial<PlannerScheduledItem>) => api.patch(`/planner/scheduled/${id}`, data),
  deleteScheduled: (id: string) => api.delete(`/planner/scheduled/${id}`),

  // Presence records (gk2control integration).
  getPresenceRecords: (params?: { dateSince?: string; dateUntil?: string; resourceId?: string; employeeNif?: string; limit?: number }) =>
    api.get('/planner/presence/records', { params }),
  syncPresence: (data: { dateSince: string; dateUntil: string; nif?: string }) =>
    api.post('/planner/presence/sync', data),
  registerManualOut: (data: { resourceId: string }) =>
    api.post('/planner/presence/manual-out', data),

  // Autofichaje del propio trabajador (VIDA-256).
  getMyPresence: () => api.get('/planner/presence/me'),
  selfClock: (data: { latitude?: number; longitude?: number; geoDenied?: boolean }) =>
    api.post('/planner/presence/me/clock', data),
  selfVoid: () => api.post('/planner/presence/me/void', {}),

  // Horarios oficiales (A1) — plantillas recurrentes con salida forzosa.
  listSchedules: (params?: { resourceId?: string }) => api.get('/planner/schedules', { params }),
  getSchedule: (id: string) => api.get(`/planner/schedules/${id}`),
  createSchedule: (data: Partial<PlannerSchedule>) => api.post('/planner/schedules', data),
  updateSchedule: (id: string, data: Partial<PlannerSchedule>) => api.patch(`/planner/schedules/${id}`, data),
  removeSchedule: (id: string) => api.delete(`/planner/schedules/${id}`),

  // Parte de horas: marcaje real vs contable por día.
  getTimesheet: (params: { nif: string; from: string; to: string }) =>
    api.get('/planner/presence/timesheet', { params }),

  // Proyectos de asignación + disponibilidad (A2)
  listProjects: (params?: { status?: string }) => api.get('/planner/projects', { params }),
  createProject: (data: Partial<AllocationProject>) => api.post('/planner/projects', data),
  updateProject: (id: string, data: Partial<AllocationProject>) => api.patch(`/planner/projects/${id}`, data),
  removeProject: (id: string) => api.delete(`/planner/projects/${id}`),

  listAvailability: (params?: { resourceId?: string; projectId?: string; from?: string; to?: string }) =>
    api.get('/planner/availability', { params }),
  createAvailability: (data: Partial<ResourceAvailability>) => api.post('/planner/availability', data),
  updateAvailability: (id: string, data: Partial<ResourceAvailability>) => api.patch(`/planner/availability/${id}`, data),
  removeAvailability: (id: string) => api.delete(`/planner/availability/${id}`),

  getAllocationReport: (params: { from: string; to: string }) =>
    api.get('/planner/presence/allocation-report', { params }),

  // Absences (kind='absence' inside ScheduledItem). One record per (resource × day).
  createAbsences: (data: {
    resourceIds: string[];
    dateFrom: string;  // YYYY-MM-DD
    dateTo: string;    // YYYY-MM-DD
    absenceKind: 'vacations' | 'personal' | 'sick' | 'permission' | 'other';
    status?: 'approved' | 'pending';
    pattern: 'full' | 'morning' | 'afternoon' | 'custom';
    hourStart?: string; // HH:MM, required if pattern='custom'
    hourEnd?: string;   // HH:MM
    notes?: string;
  }) => api.post('/planner/absences', data),
  approveAbsence: (id: string) => api.patch(`/planner/absences/${id}/approve`),
  rejectAbsence: (id: string, data?: { reason?: string }) =>
    api.patch(`/planner/absences/${id}/reject`, data || {}),
  deleteAbsence: (id: string) => api.delete(`/planner/absences/${id}`),
};

export interface PresenceRecord {
  _id: string;
  organization_id: string;
  resourceId?: string | null;
  employeeNif: string;
  date: string;
  type: 'in' | 'out';
  latitude?: string | null;
  longitude?: string | null;
  source: 'gk2control' | 'auto' | 'manual';
  externalId?: string | null;
  created_at: string;
}

// Processes API (Module B). Org-scoped via the X-Org-Id header.
export interface ProcessPhase {
  name: string;
  dataIn?: string;
  procedure?: string;
  dataOut?: string;
  roles: string[];
  destinations: string[];
  phaseNodeId?: string | null;
  stateNodeId?: string | null;
}
export interface AppProcess {
  _id: string;
  organization_id: string;
  name: string;
  objective?: string;
  description?: string;
  ambitoNodeId?: string | null;
  status: 'draft' | 'review' | 'active' | 'archived';
  phases: ProcessPhase[];
  aiInstruction?: string;
  updated_at?: string;
}

export const processesApi = {
  list: (status?: string) => api.get('/processes', { params: status ? { status } : undefined }),
  get: (id: string) => api.get(`/processes/${id}`),
  create: (data: Partial<AppProcess>) => api.post('/processes', data),
  update: (id: string, data: Partial<AppProcess>) => api.patch(`/processes/${id}`, data),
  remove: (id: string) => api.delete(`/processes/${id}`),
  // B-P1: AI draft of phases or the instruction from the ficha.
  aiSuggest: (data: { target: 'phases' | 'instruction'; name: string; objective?: string; description?: string }) =>
    api.post('/processes/ai-suggest', data),
};

// Notifications API (project-wide). Org-scoped via the X-Org-Id header.
export interface AppNotification {
  _id: string;
  organizationId: string;
  userId?: string | null;
  module: string;
  type: string;
  title: string;
  body?: string;
  level: 'info' | 'warning' | 'critical';
  ref_type?: string;
  ref_id?: string;
  url?: string;
  scheduled_for?: string | null;
  status: 'pending' | 'read' | 'dismissed';
  deliveries?: {
    channel: string;
    recipient?: string;
    status: 'pending' | 'sent' | 'failed';
    attempts?: number;
    lastError?: string;
    sentAt?: string | null;
  }[];
  createdAt: string;
}

export const notificationsApi = {
  list: (limit = 50) => api.get('/notifications', { params: { limit } }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id: string) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  dismiss: (id: string) => api.delete(`/notifications/${id}`),
  // Per-user preferences for the current org (X-Org-Id).
  getPreferences: () => api.get('/notifications/preferences'),
  savePreferences: (data: { email?: { enabled?: boolean; minLevel?: 'info' | 'warning' | 'critical' } }) =>
    api.put('/notifications/preferences', data),
  // Per-role preferences for the current org (admin, org:update). Map roleId -> {email}.
  getRolePreferences: () => api.get('/notifications/role-preferences'),
  saveRolePreference: (roleId: string, data: { email?: { enabled?: boolean; minLevel?: 'info' | 'warning' | 'critical' } }) =>
    api.put(`/notifications/role-preferences/${roleId}`, data),
};

// Meetings API (modulo Reuniones IA). Org-scoped via el header X-Org-Id.
export interface Meeting {
  _id: string;
  organization_id: string;
  ownerUserId: string;
  sharedWith: string[];
  title: string;
  status: 'recording' | 'uploaded' | 'transcribing' | 'ready' | 'failed';
  audio?: {
    bucket: string;
    objectName: string;
    contentType?: string;
    sizeBytes?: number;
    durationSeconds?: number;
  };
  language?: string;
  transcript?: {
    text: string;
    engine?: string;
    model?: string;
    language?: string;
    diarized?: boolean;
    segments?: { start?: number; end?: number; text: string; speaker?: string }[];
    completedAt?: string;
  };
  summary?: {
    text: string;
    keyPoints?: { topic: string; points: string[] }[];
    agreements?: string[];
    tasks?: string[];
    decisions?: string[];
    risks?: string[];
    model?: string;
    completedAt?: string;
  };
  error?: string;
  created_at: string;
  updated_at: string;
}

export const meetingsApi = {
  list: (params?: { page?: number; limit?: number; scope?: 'all' | 'mine' | 'shared' }) =>
    api.get('/meetings', { params }),
  get: (id: string) => api.get(`/meetings/${id}`),
  create: (data: { title: string; language?: string }) => api.post('/meetings', data),
  // Sube el binario del audio (streaming a traves del backend hasta minio-meetings).
  uploadAudio: (id: string, blob: Blob) =>
    api.put(`/meetings/${id}/audio`, blob, {
      headers: { 'Content-Type': blob.type || 'application/octet-stream' },
    }),
  // Encola transcripcion + resumen IA.
  complete: (id: string) => api.post(`/meetings/${id}/complete`),
  share: (id: string, userIds: string[]) => api.post(`/meetings/${id}/share`, { userIds }),
  unshare: (id: string, userId: string) => api.delete(`/meetings/${id}/share/${userId}`),
  remove: (id: string) => api.delete(`/meetings/${id}`),
  orgUsers: () => api.get('/meetings/org-users'),
  // Descarga el audio como blob (el <audio> no puede llevar el header de auth).
  audioBlob: (id: string) => api.get(`/meetings/${id}/audio`, { responseType: 'blob' }),
};

// CRM API (contactos multi-fuente, empresas). Org-scoped via X-Org-Id.
export type ContactSource = 'ecommerce' | 'marketplace' | 'manual' | 'api_import' | 'web_form' | 'other';

export interface CrmCompany {
  _id: string;
  organization_id: string;
  name: string;
  taxId?: string;
  type?: 'cliente' | 'proveedor' | 'partner' | 'prospect';
  segment?: string;
  website?: string;
  phones: string[];
  emails: string[];
  ownerUserId?: string;
  circles: string[];
  tags: string[];
  notes?: string;
  created_at: string;
  updated_at: string;
}

export type ContactKind = 'person' | 'company';

// Ficha del lead (VIDA-355)
export type CommercialContactRole = 'prescriptor' | 'direccion' | 'tejedor';
export type SocialNetwork = 'facebook' | 'instagram' | 'linkedin' | 'x' | 'youtube' | 'tiktok';
export interface CrmCommercialContact {
  role: CommercialContactRole;
  name?: string;
  phone?: string;
  email?: string;
  internalNotes?: string;
}
export interface CrmContactEntity {
  legalName?: string;
  legalForm?: string;
  registryNumber?: string;
  branding?: { logoUrl?: string; iconUrl?: string; primaryColor?: string };
  publicContact?: {
    address?: string; website?: string; email?: string; phone?: string;
    social?: Partial<Record<SocialNetwork, string>>;
  };
}
export type LeadProjectStatus = 'abierto' | 'en_propuesta' | 'aceptado' | 'descartado';

// Captación (VIDA-356)
export type LeadInterest = 'productos' | 'proyecto' | 'otro';
export type LeadFieldType = 'text' | 'textarea' | 'url' | 'color' | 'boolean';
export interface LeadFieldDef { key: string; label: string; group: string; type: LeadFieldType; max?: number }
export interface LeadCaptureSettings { catalog_url?: string; notify_email?: string }
export type LeadLinkStatus = 'activo' | 'caducado' | 'anulado';
export interface CrmLeadLink {
  _id: string;
  projectId?: string | null;
  fields: string[];
  expiresAt: string;
  revokedAt?: string | null;
  sentTo?: string;
  sentAt?: string | null;
  lastUsedAt?: string | null;
  submissions?: number;
  status: LeadLinkStatus;
  created_at?: string;
}
export interface CrmLeadResponse { _id: string; projectId?: string | null; values: Record<string, string | boolean>; created_at: string }
export interface PublicLeadField extends LeadFieldDef { value: string | boolean }
export interface PublicLeadLink {
  org: { name: string; logoUrl: string | null };
  greetingName: string | null;
  projectName: string | null;
  expiresAt: string;
  fields: PublicLeadField[];
}
export interface CrmLeadProject {
  _id: string;
  contactId: string;
  name: string;
  status: LeadProjectStatus;
  client: {
    description?: string;
    objectives?: { corporate?: string; social?: string; economic?: string; environmental?: string };
    cnae?: string;
    activityRegistered?: boolean;
    legalUrls?: { legalNotice?: string; privacy?: string; cookies?: string };
    vidaTermsAccepted?: boolean;
    vidaTermsAcceptedAt?: string | null;
  };
  internal: {
    appreciations?: string;
    criticalPoints?: string;
    solutions?: { essential?: string[]; recommended?: string[]; useful?: string[] };
    keyProcesses?: string;
    roadmap?: string;
  };
  created_at: string;
  updated_at: string;
}
export type CrmLeadProjectInput = Partial<Pick<CrmLeadProject, 'name' | 'status' | 'client' | 'internal'>>;

export interface CrmContact {
  _id: string;
  organization_id: string;
  kind: ContactKind;
  customerId?: string | null;
  companyId?: string | null;
  first_name?: string;
  last_name?: string;
  position?: string;
  companyName?: string;
  taxId?: string;
  website?: string;
  email?: string;
  phone?: string;
  type: 'lead' | 'prospect' | 'customer';
  stage?: string;
  segment?: string;
  circles: string[];
  source: ContactSource;
  sourceRef?: string;
  ownerUserId?: string;
  tags: string[];
  roles?: string[];
  leadInterest?: LeadInterest;
  entity?: CrmContactEntity;
  commercialContacts?: CrmCommercialContact[];
  notes?: string;
  lastContactedAt?: string;
  customFields?: {
    ai?: {
      summary?: string; segment?: string; interests?: string[]; recommendations?: string[];
      generatedAt?: string; model?: string;
    };
    [k: string]: unknown;
  };
  created_at: string;
  updated_at: string;
}

export interface CrmAiEnrichment {
  resumen: string;
  segmento: string;
  intereses: string[];
  recomendaciones: string[];
}

// Item de la lista UNIFICADA de contactos (CRM + clientes ecommerce sin contacto).
export interface CrmDirectoryItem {
  _id: string;
  kind: ContactKind; // persona o empresa
  source: ContactSource;
  type: 'lead' | 'prospect' | 'customer';
  stage?: string;
  first_name?: string;
  last_name?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  companyId?: string | null;
  customerId?: string | null;
  total_orders?: number;
  total_spent?: number;
  created_at?: string;
  customerOnly?: boolean; // true = cliente ecommerce aún sin ficha CRM
}

// Propuestas / presupuestos comerciales (B2)
export type ProposalStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
export interface CrmProposalLine {
  concept: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  discountPct: number; // 0-100
  taxPct: number; // 0-100 (IVA)
  lineTotal: number;
}
export interface CrmProposal {
  _id: string;
  organization_id: string;
  contactId: string;
  companyId?: string | null;
  customerId?: string | null;
  number: number;
  title: string;
  currency: string;
  lines: CrmProposalLine[];
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  total: number;
  status: ProposalStatus;
  validUntil?: string;
  sentAt?: string;
  decidedAt?: string;
  ownerUserId?: string;
  notes?: string;
  terms?: string;
  created_at: string;
  updated_at: string;
}
export interface CrmProposalInput {
  contactId?: string;
  title?: string;
  currency?: string;
  lines?: Array<Pick<CrmProposalLine, 'concept' | 'quantity' | 'unitPrice'> & Partial<CrmProposalLine>>;
  validUntil?: string;
  notes?: string;
  terms?: string;
}

// Comunicaciones / timeline (B3)
export type CommunicationType = 'call' | 'email' | 'note' | 'whatsapp' | 'meeting' | 'promo';
export type CommunicationDirection = 'in' | 'out' | 'internal';
export interface CrmCommunication {
  _id: string;
  organization_id: string;
  contactId: string;
  companyId?: string | null;
  customerId?: string | null;
  type: CommunicationType;
  direction: CommunicationDirection;
  occurredAt: string;
  subject?: string;
  body?: string;
  authorUserId?: string;
  relatedProposalId?: string;
  created_at: string;
  updated_at: string;
}
export interface CrmCommunicationInput {
  contactId?: string;
  type?: CommunicationType;
  direction?: CommunicationDirection;
  occurredAt?: string;
  subject?: string;
  body?: string;
  relatedProposalId?: string;
}

// Actividad unificada + enlace planificador (B4)
export type CrmActivityEntry =
  | { source: 'communication'; at: string; id: string; commType: CommunicationType; direction: CommunicationDirection; subject?: string; body?: string }
  | { source: 'proposal'; at: string; id: string; number: number; title: string; total: number; currency: string; status: ProposalStatus }
  | { source: 'appointment'; at: string; id: string; start: string; end: string; itemKind?: string; title?: string; notes?: string };
export interface CrmActivityResponse {
  timeline: CrmActivityEntry[];
  metrics: { total_orders?: number; total_spent?: number; last_order_at?: string } | null;
  appointmentsAvailable: boolean;
  linkedToCustomer: boolean;
}

export interface CrmStage { id: string; name: string; color?: string; order: number }
export interface CrmPipelineCard {
  _id: string; kind: ContactKind; type: 'lead' | 'prospect' | 'customer'; stage: string | null;
  source: ContactSource; first_name?: string; last_name?: string; companyName?: string; email?: string;
}

// Solicitudes de colaborador («tejedores») llegadas desde las tiendas (VIDA-307)
export const weaversApi = {
  listApplications: (params?: { status?: 'pending' | 'approved' | 'rejected' | 'all'; limit?: number }) =>
    api.get('/weavers/applications', { params }),
  resolveApplication: (id: string, data: { status: 'approved' | 'rejected' | 'pending'; review_notes?: string }) =>
    api.patch(`/weavers/applications/${id}`, data),
  // Solo super_admin: eliminar una solicitud (para limpiar pruebas).
  deleteApplication: (id: string) => api.delete(`/weavers/applications/${id}`),
};

// Colaboradores (Tejedores) — la ficha de la RELACIÓN con cada tejedor (VIDA-297).
// Distinto de weaversApi, que atiende las solicitudes de alta.
export interface CollaboratorLink {
  _id: string;
  weaver_org_id: string;
  status: 'invited' | 'active' | 'paused' | 'ended';
  alias?: string;
  since?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  commission_pct?: number;
  categorias: string[];
  distintivos: string[];
  tags: string[];
  notes?: string;
  created_at: string;
  updated_at: string;
  weaver?: {
    _id: string;
    name: string;
    slug: string;
    logo_url?: string;
    public_info?: Record<string, unknown>;
  } | null;
}

export const collaboratorsApi = {
  list: (params?: { status?: CollaboratorLink['status'] | 'all' }) =>
    api.get('/collaborators', { params }),
  get: (id: string) => api.get(`/collaborators/${id}`),
  create: (data: Partial<CollaboratorLink> & { weaver_org_id: string }) =>
    api.post('/collaborators', data),
  update: (id: string, data: Partial<CollaboratorLink>) =>
    api.patch(`/collaborators/${id}`, data),
  remove: (id: string) => api.delete(`/collaborators/${id}`),
  products: (id: string) => api.get(`/collaborators/${id}/products`),
  // Invita al responsable del tejedor a su propio panel (rol weaver_admin).
  invite: (id: string, email?: string) => api.post(`/collaborators/${id}/invite`, { email }),
  // Categorías y distintivos disponibles para las fichas de la org (selector F3).
  taxonomy: () => api.get('/collaborators/taxonomy'),
};

export interface CollaboratorTaxonomyNode {
  id: string;
  name: string;
  parent: string | null;
  depth: number;
}

export interface CrmRoleNode {
  id: string;
  code: string;
  name: string;
  parent: string | null;
  depth: number;
}

// Páginas públicas de captación (sin sesión)
export const publicLeadsApi = {
  info: (orgSlug: string) => api.get<{ name: string; logoUrl: string | null }>(`/public/leads/${encodeURIComponent(orgSlug)}/info`),
  request: (orgSlug: string, data: { email: string; name?: string; interest: LeadInterest; comment?: string; fax?: string }) =>
    api.post(`/public/leads/${encodeURIComponent(orgSlug)}/request`, data),
  getLink: (orgSlug: string, token: string) =>
    api.get<PublicLeadLink>(`/public/leads/${encodeURIComponent(orgSlug)}/link/${encodeURIComponent(token)}`),
  submitLink: (orgSlug: string, token: string, values: Record<string, string | boolean>, fax?: string) =>
    api.post(`/public/leads/${encodeURIComponent(orgSlug)}/link/${encodeURIComponent(token)}`, { values, fax }),
};

export const crmApi = {
  // Árbol de roles funcionales (VIDA-354)
  getRolesTaxonomy: () => api.get<{ roles: CrmRoleNode[] }>('/crm/roles-taxonomy'),
  // Proyectos del lead (VIDA-355)
  listProjects: (contactId: string) => api.get<{ projects: CrmLeadProject[] }>(`/crm/contacts/${contactId}/projects`),
  createProject: (contactId: string, data: CrmLeadProjectInput) =>
    api.post<{ project: CrmLeadProject }>(`/crm/contacts/${contactId}/projects`, data),
  updateProject: (id: string, data: CrmLeadProjectInput) => api.patch<{ project: CrmLeadProject }>(`/crm/projects/${id}`, data),
  removeProject: (id: string) => api.delete(`/crm/projects/${id}`),
  // Captación (VIDA-356)
  getLeadCaptureSettings: () => api.get<{ settings: LeadCaptureSettings; orgSlug: string }>('/crm/lead-capture/settings'),
  saveLeadCaptureSettings: (data: LeadCaptureSettings) => api.put<{ settings: LeadCaptureSettings }>('/crm/lead-capture/settings', data),
  getLeadFields: () => api.get<{ fields: LeadFieldDef[] }>('/crm/lead-fields'),
  listLeadLinks: (contactId: string) => api.get<{ links: CrmLeadLink[] }>(`/crm/contacts/${contactId}/lead-links`),
  createLeadLink: (contactId: string, data: { fields: string[]; projectId?: string | null; send: boolean }) =>
    api.post<{ link: CrmLeadLink; url: string; sent: boolean }>(`/crm/contacts/${contactId}/lead-links`, data),
  revokeLeadLink: (id: string) => api.post<{ link: CrmLeadLink }>(`/crm/lead-links/${id}/revoke`),
  listLeadResponses: (contactId: string) => api.get<{ responses: CrmLeadResponse[] }>(`/crm/contacts/${contactId}/lead-responses`),
  // Contactos (lista UNIFICADA: contactos CRM + clientes ecommerce)
  listContacts: (params?: {
    page?: number; limit?: number; search?: string;
    source?: ContactSource; type?: string; kind?: ContactKind; companyId?: string; linked?: 'yes' | 'no';
  }) => api.get('/crm/contacts', { params }),
  getContact: (id: string) => api.get(`/crm/contacts/${id}`),
  createContact: (data: Partial<CrmContact>) => api.post('/crm/contacts', data),
  updateContact: (id: string, data: Partial<CrmContact>) => api.patch(`/crm/contacts/${id}`, data),
  removeContact: (id: string) => api.delete(`/crm/contacts/${id}`),
  linkCustomer: (id: string, customerId: string | null) =>
    api.post(`/crm/contacts/${id}/link-customer`, { customerId }),
  // Enriquecido con IA (analiza historial → resumen/segmento/intereses/recomendaciones)
  enrich: (id: string) => api.post(`/crm/contacts/${id}/enrich`),
  // Pipeline / fases (kanban, configurable por org)
  getStages: () => api.get('/crm/stages'),
  saveStages: (stages: { id: string; name: string; color?: string; order: number }[]) =>
    api.put('/crm/stages', { stages }),
  getPipeline: () => api.get('/crm/pipeline'),
  // Propuestas / presupuestos (B2)
  listProposals: (contactId: string) => api.get(`/crm/contacts/${contactId}/proposals`),
  getProposal: (id: string) => api.get(`/crm/proposals/${id}`),
  createProposal: (data: CrmProposalInput) => api.post('/crm/proposals', data),
  updateProposal: (id: string, data: CrmProposalInput) => api.patch(`/crm/proposals/${id}`, data),
  setProposalStatus: (id: string, status: ProposalStatus) => api.patch(`/crm/proposals/${id}/status`, { status }),
  removeProposal: (id: string) => api.delete(`/crm/proposals/${id}`),
  // Comunicaciones / timeline (B3)
  listCommunications: (contactId: string) => api.get(`/crm/contacts/${contactId}/communications`),
  createCommunication: (data: CrmCommunicationInput) => api.post('/crm/communications', data),
  updateCommunication: (id: string, data: CrmCommunicationInput) => api.patch(`/crm/communications/${id}`, data),
  removeCommunication: (id: string) => api.delete(`/crm/communications/${id}`),
  // Actividad unificada (B4): métricas ecommerce + citas del planificador + timeline
  getActivity: (contactId: string) => api.get(`/crm/contacts/${contactId}/activity`),
  byCustomer: (customerId: string) => api.get(`/crm/by-customer/${customerId}`),
  // Empresas
  listCompanies: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get('/crm/companies', { params }),
  getCompany: (id: string) => api.get(`/crm/companies/${id}`),
  createCompany: (data: Partial<CrmCompany>) => api.post('/crm/companies', data),
  updateCompany: (id: string, data: Partial<CrmCompany>) => api.patch(`/crm/companies/${id}`, data),
  removeCompany: (id: string) => api.delete(`/crm/companies/${id}`),
};

// Products API (VIDA Products)
export const productsApi = {
  getProducts: (page = 1, limit = 20, filters?: {
    type?: string; status?: string; tipoProducto?: string;
    search?: string; ecommerce_active?: boolean; catalog_active?: boolean; tag?: string;
    parent_id?: string; project_id?: string;
    sortBy?: 'name' | 'tipoProducto' | 'pvpFinal' | 'updated_at';
    sortOrder?: 'asc' | 'desc';
  }) =>
    api.get('/products/products', { params: { page, limit, ...filters } }),

  getProduct: (id: string) =>
    api.get(`/products/products/${id}`),

  createProduct: (data: object) =>
    api.post('/products/products', data),

  updateProduct: (id: string, data: object) =>
    api.patch(`/products/products/${id}`, data),

  deleteProduct: (id: string) =>
    api.delete(`/products/products/${id}`),

  changeStatus: (id: string, status: string) =>
    api.patch(`/products/products/${id}/status`, { status }),

  stats: () =>
    api.get('/products/stats'),

  // Importación masiva desde Excel (dryRun = previsualización sin escribir).
  // mode 'update' + updateSkus = actualizar los existentes aceptados.
  importProducts: (items: unknown[], dryRun: boolean, opts?: { mode?: 'skip' | 'update'; updateSkus?: string[] }) =>
    api.post('/products/products/import', { items, dryRun, mode: opts?.mode, updateSkus: opts?.updateSkus }),

  getUploadUrl: (productId: string, filename: string) =>
    api.post(`/products/products/${productId}/images/upload-url`, { filename }),

  deleteImage: (productId: string, imageId: string) =>
    api.delete(`/products/products/${productId}/images/${imageId}`),

  reorderImages: (productId: string, imageIds: string[]) =>
    api.patch(`/products/products/${productId}/images/reorder`, { imageIds }),

  updateCategorization: (productId: string, data: Record<string, string[]>) =>
    api.patch(`/products/products/${productId}/categorization`, data),

  // Composite product components
  searchSimpleProducts: (query?: string, excludeIds?: string[]) =>
    api.get('/products/products/search/simple', {
      params: { q: query || '', exclude: excludeIds?.join(',') || '' },
    }),

  getComponents: (productId: string) =>
    api.get(`/products/products/${productId}/components`),

  addComponents: (productId: string, components: Array<{
    product_id: string; quantity: number; label?: string;
    isOptional?: boolean; priceOverride?: number;
  }>) =>
    api.post(`/products/products/${productId}/components`, { components }),

  removeComponent: (productId: string, componentProductId: string) =>
    api.delete(`/products/products/${productId}/components/${componentProductId}`),

  // Collaborative product management
  searchOrganizations: (query?: string) =>
    api.get('/products/organizations/search', { params: { q: query || '' } }),

  // Tejedores con los que colabora esta org + sus productos ofrecidos a la red,
  // como opciones al montar un producto colaborativo (Tejedores F5).
  collaboratorsOptions: () => api.get('/products/collaborators-options'),

  updateCollaborators: (productId: string, data: {
    collaborators: Array<{
      organization_id: string; role: 'owner' | 'editor';
      contribution: number; label?: string;
    }>;
    visibilityLevel?: string;
  }) =>
    api.patch(`/products/products/${productId}/collaborators`, data),

  updateContributorProducts: (productId: string, contributorProducts: Array<{
    product_id: string; organization_id: string;
    quantity: number; label?: string;
  }>) =>
    api.patch(`/products/products/${productId}/contributor-products`, { contributorProducts }),

  getCollaborativeView: (productId: string) =>
    api.get(`/products/products/${productId}/collaborative`),
};

// Content API (CMS)
export const contentApi = {
  getSections: (orgId?: string) =>
    api.get('/content/sections', { params: orgId ? { orgId } : undefined }),

  saveSections: (orgId: string, sections: any[]) =>
    api.put('/content/sections', { sections, organizationId: orgId }),

  getContents: (orgId: string, section?: string, locale: string = 'es', page = 1, limit = 200) =>
    api.get('/content', { params: { orgId, section, locale, page, limit } }),

  getContent: (orgId: string, id: string) =>
    api.get(`/content/${id}`, { params: { orgId } }),

  createContent: (orgId: string, data: { section: string; component: string; content: Record<string, any>; locale?: string; order?: number }) =>
    api.post('/content', { ...data, organizationId: orgId }),

  updateContent: (orgId: string, id: string, data: { section?: string; component?: string; content?: Record<string, any>; locale?: string; order?: number }) =>
    api.put(`/content/${id}`, { ...data, organizationId: orgId }),

  deleteContent: (orgId: string, id: string) =>
    api.delete(`/content/${id}`, { params: { orgId } }),

  getUploadUrl: (orgId: string, objectName: string) =>
    api.post('/content/upload', { objectName, organizationId: orgId }),

  reorderContents: (orgId: string, items: { id: string; order: number }[]) =>
    api.patch('/content/reorder', { items, organizationId: orgId }),

  duplicateContent: (orgId: string, id: string) =>
    api.post(`/content/${id}/duplicate`, { organizationId: orgId }),

  getNavConfig: () => api.get('/content/nav-config'),
  saveNavConfig: (config: { items: any[] }) => api.put('/content/nav-config', config),
  getFooterConfig: () => api.get('/content/footer-config'),
  saveFooterConfig: (config: object) => api.put('/content/footer-config', config),
  getCatalogSiteConfig: (catalog?: string | null) =>
    api.get('/content/catalog-site-config', { params: catalog ? { catalog } : {} }),
  saveCatalogSiteConfig: (config: object, catalog?: string | null) =>
    api.put('/content/catalog-site-config', config, { params: catalog ? { catalog } : {} }),
  getEcommerceSiteConfig: () => api.get('/content/ecommerce-site-config'),
  saveEcommerceSiteConfig: (config: object) => api.put('/content/ecommerce-site-config', config),
  getShippingMethods: () => api.get('/content/shipping-methods'),
  saveShippingMethods: (config: object) => api.put('/content/shipping-methods', config),
  getPetSiteConfig: () => api.get('/content/pet-site-config'),
  savePetSiteConfig: (config: object) => api.put('/content/pet-site-config', config),
};

// Metadata API (Hierarchical permissions system)
export const metadataApi = {
  // Import one or more org trees (nodes + associations). dryRun=true → preview only.
  importTree: (payload: unknown, dryRun: boolean) =>
    api.post('/metadata/import-tree', { payload, dryRun }),

  // Domains
  getDomains: () =>
    api.get('/metadata/domains'),

  getDomain: (id: string) =>
    api.get(`/metadata/domains/${id}`),

  createDomain: (data: { id: string; name: string; description?: string; icon?: string; order?: number }) =>
    api.post('/metadata/domains', data),

  updateDomain: (id: string, data: { name?: string; description?: string; icon?: string; order?: number }) =>
    api.patch(`/metadata/domains/${id}`, data),

  deleteDomain: (id: string) =>
    api.delete(`/metadata/domains/${id}`),

  // Trees. forProducts=true → only the trees the product editor should show for
  // the current org (org's configured list, or the "categorization" domain).
  getTrees: (domainId?: string, forProducts?: boolean) =>
    api.get('/metadata/trees', { params: { domainId, forProducts: forProducts ? 1 : undefined } }),

  getTree: (id: string, orgId?: string) =>
    api.get(`/metadata/trees/${id}`, { params: { orgId } }),

  createTree: (data: { id: string; domain_id: string; name: string; description?: string; base_permission?: string; order?: number; organization_id?: string | null }) =>
    api.post('/metadata/trees', data),

  updateTree: (id: string, data: { name?: string; description?: string; base_permission?: string; order?: number }) =>
    api.patch(`/metadata/trees/${id}`, data),

  deleteTree: (id: string) =>
    api.delete(`/metadata/trees/${id}`),

  // Nodes
  getNodes: (treeId?: string, parentId?: string) =>
    api.get('/metadata/nodes', { params: { treeId, parentId } }),

  getNode: (id: string) =>
    api.get(`/metadata/nodes/${id}`),

  createNode: (data: { id: string; tree_id: string; parent_id?: string | null; name: string; description?: string; code?: string; base_permission?: string; order?: number }) =>
    api.post('/metadata/nodes', data),

  updateNode: (id: string, data: { name?: string; description?: string; code?: string; base_permission?: string; order?: number }) =>
    api.patch(`/metadata/nodes/${id}`, data),

  moveNode: (id: string, data: { parent_id: string | null; order?: number }) =>
    api.post(`/metadata/nodes/${id}/move`, data),

  deleteNode: (id: string) =>
    api.delete(`/metadata/nodes/${id}`),

  // Grants (permission delegation)
  getGrants: (targetType?: string, targetId?: string, granteeType?: string, granteeId?: string) =>
    api.get('/metadata/grants', { params: { targetType, targetId, granteeType, granteeId } }),

  createGrant: (data: { grantee_type: 'role' | 'user'; grantee_id: string; target_type: 'domain' | 'tree' | 'node'; target_id: string; level: string; cascade?: boolean }) =>
    api.post('/metadata/grants', data),

  revokeGrant: (id: string) =>
    api.delete(`/metadata/grants/${id}`),

  // Values (custom per-scope values)
  getValues: (nodeId?: string, scopeType?: string, scopeId?: string) =>
    api.get('/metadata/values', { params: { nodeId, scopeType, scopeId } }),

  createValue: (data: { node_id: string; scope_type: string; scope_id?: string | null; custom_name?: string; custom_description?: string; metadata?: Record<string, unknown> }) =>
    api.post('/metadata/values', data),

  updateValue: (id: string, data: { custom_name?: string; custom_description?: string; metadata?: Record<string, unknown>; is_enabled?: boolean }) =>
    api.patch(`/metadata/values/${id}`, data),

  deleteValue: (id: string) =>
    api.delete(`/metadata/values/${id}`),

  // Utilities
  checkPermission: (nodeId: string) =>
    api.get('/metadata/check-permission', { params: { nodeId } }),
};

// Webhooks API
export const webhooksApi = {
  list: (page = 1, limit = 20) =>
    api.get('/webhooks', { params: { page, limit } }),

  get: (id: string) =>
    api.get(`/webhooks/${id}`),

  create: (data: { url: string; events: string[]; description?: string; organization_id?: string }) =>
    api.post('/webhooks', data),

  update: (id: string, data: { url?: string; events?: string[]; description?: string; active?: boolean }) =>
    api.put(`/webhooks/${id}`, data),

  delete: (id: string) =>
    api.delete(`/webhooks/${id}`),

  getDeliveries: (id: string, page = 1, limit = 20) =>
    api.get(`/webhooks/${id}/deliveries`, { params: { page, limit } }),

  test: (id: string) =>
    api.post(`/webhooks/${id}/test`),
};

// API Keys API
export const apiKeysApi = {
  list: (page = 1, limit = 20) =>
    api.get('/apikeys', { params: { page, limit } }),

  get: (id: string) =>
    api.get(`/apikeys/${id}`),

  create: (data: { name: string; service?: string; permissions?: string[]; rate_limit?: number; organization_id?: string; expires_at?: string }) =>
    api.post('/apikeys', data),

  update: (id: string, data: { name?: string; permissions?: string[]; rate_limit?: number; active?: boolean }) =>
    api.put(`/apikeys/${id}`, data),

  delete: (id: string) =>
    api.delete(`/apikeys/${id}`),

  // Per-org endpoints
  listForOrg: (orgId: string) =>
    api.get(`/apikeys/org/${orgId}`),

  createForOrg: (orgId: string, data: { name: string; service: string; permissions?: string[]; rate_limit?: number; expires_at?: string }) =>
    api.post(`/apikeys/org/${orgId}`, data),

  revokeForOrg: (orgId: string, keyId: string) =>
    api.delete(`/apikeys/org/${orgId}/${keyId}`),

  // Rota la key en el mismo registro y devuelve el valor en claro UNA sola vez.
  // La key anterior deja de funcionar de inmediato (hay que actualizar Vercel).
  regenerateForOrg: (orgId: string, keyId: string) =>
    api.post(`/apikeys/org/${orgId}/${keyId}/regenerate`),

  getScopes: () =>
    api.get('/apikeys/scopes'),
};

// Projects API (VIDA Projects)
export const projectsApi = {
  list: (page = 1, limit = 20, filters?: { status?: string; search?: string }) =>
    api.get('/projects/list', { params: { page, limit, ...filters } }),

  get: (id: string) =>
    api.get(`/projects/${id}`),

  create: (data: { name: string; description?: string; allowed_nodes?: string[]; assigned_users?: string[] }) =>
    api.post('/projects', data),

  update: (id: string, data: { name?: string; description?: string; status?: string; allowed_nodes?: string[] }) =>
    api.patch(`/projects/${id}`, data),

  delete: (id: string) =>
    api.delete(`/projects/${id}`),

  assignUsers: (id: string, userIds: string[]) =>
    api.patch(`/projects/${id}/assign-users`, { user_ids: userIds }),

  unassignUser: (id: string, userId: string) =>
    api.delete(`/projects/${id}/users/${userId}`),

  getMyNodes: () =>
    api.get('/projects/my-nodes'),
};

// Dashboard overview (user-facing home). All blocks come from the same call.
export type DashboardOverview = {
  // Permisos DEL USUARIO EN LA ORG ACTIVA (VIDA-294). Usar estos para pintar la
  // home, no los del store de auth: aquellos son la unión de TODAS sus orgs.
  permissions?: string[];
  organization: {
    id: string;
    name?: string;
    slug: string;
    domain: string | null;
    welcome_text?: string;
    public_info?: {
      description?: string;
      email?: string;
      phone?: string;
      address?: string;
    } | null;
    features: {
      ecommerce?: boolean;
      catalog?: boolean;
      blog?: boolean;
      planner?: boolean;
      processes?: boolean;
      marketing?: boolean;
      multilingual?: boolean;
      analytics?: boolean;
    };
  };
  kpis: {
    users?: number;
    members?: number;
    catalogProducts?: number;
    blogPostsPublished?: number;
    ordersThisMonth?: number;
    revenueThisMonthCents?: number;
    projectsActive?: number;
    processesActive?: number;
    upcomingAlarms7d?: number;
    unreadAnnouncements?: number;
  };
  announcements: Array<{
    _id: string;
    title: string;
    body?: string;
    level?: 'info' | 'warning' | 'critical';
    publicWeb?: boolean;
    url?: string;
    createdAt: string;
    scheduled_for?: string | null;
    status?: 'pending' | 'read' | 'dismissed';
  }>;
  meetings: Array<{
    start: string;
    end: string;
    notes?: string;
    location?: string;
    title?: string;
  }>;
  budget: null | {
    year: number;
    plannedIncomeCents: number;
    plannedExpenseCents: number;
    actualIncomeCents: number;
    actualExpenseCents: number;
    balanceCents: number;
  };
};

export const dashboardApi = {
  getOverview: () => api.get<{ data: DashboardOverview }>('/dashboard/overview'),
};

// ============================================================
// Management — Asociados (Members)
// ============================================================
// Backend en backend/src/routes/management.routes.ts. Cada submodulo se puede
// activar/desactivar por organizacion via `config.management.<sub>.enabled`.

export type MemberStatus = 'active' | 'pending' | 'inactive';

export interface Member {
  _id: string;
  organization_id: string;
  fullName: string;
  nif?: string;
  email?: string;
  phone?: string;
  address?: string;
  joinedAt?: string;
  status: MemberStatus;
  feePlanId?: string | null;
  iban?: string;
  notes?: string;
  user_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface MemberStats {
  total: number;
  active: number;
  pending: number;
  inactive: number;
  withDashboard: number;
}

// ============================================================
// Management — Presupuesto (Budgets / Lines / Entries / Summary)
// ============================================================

export type BudgetStatus = 'draft' | 'approved' | 'closed';
export type BudgetLineKind = 'income' | 'expense';
export type BudgetEntryStatus = 'paid' | 'pending';
export type BudgetEntryMethod =
  | 'transferencia'
  | 'efectivo'
  | 'bizum'
  | 'tarjeta'
  | 'domiciliado'
  | 'otro';

export interface Budget {
  _id: string;
  organization_id: string;
  year: number;
  status: BudgetStatus;
  currency: string;
  notes?: string;
  approvedAt?: string;
  approvedBy?: string;
  closedAt?: string;
  created_at: string;
  updated_at: string;
}

export interface BudgetLine {
  _id: string;
  organization_id: string;
  budgetId: string;
  kind: BudgetLineKind;
  category: string;
  label: string;
  plannedAmount: number; // centimos
  notes?: string;
  order?: number;
  created_at: string;
  updated_at: string;
}

export interface BudgetEntry {
  _id: string;
  organization_id: string;
  budgetId: string;
  budgetLineId: string;
  kind: BudgetLineKind;
  status: BudgetEntryStatus;
  date: string;
  amount: number;
  label: string;
  payee: string;
  method: BudgetEntryMethod;
  reference?: string;
  memberId?: string;
  memberFeeStatusId?: string;
  attachmentUrl?: string;
  notes?: string;
  createdBy: string;
  created_at: string;
  updated_at: string;
}

export interface BudgetSummary {
  budget: Budget;
  totals: {
    income: { paid: number; pending: number; planned: number; total: number };
    expense: { paid: number; pending: number; planned: number; total: number };
  };
  net: { paid: number; projected: number; planned: number };
  lines: Array<
    Omit<BudgetLine, 'created_at' | 'updated_at'> & {
      executed: { paid: number; pending: number; total: number; entriesCount: number };
    }
  >;
}

export const budgetsApi = {
  list: () => api.get<{ budgets: Budget[] }>('/management/budgets'),
  create: (data: { year: number; currency?: string; notes?: string }) =>
    api.post<{ budget: Budget }>('/management/budgets', data),
  get: (ref: string | number) =>
    api.get<{ budget: Budget }>(`/management/budgets/${ref}`),
  update: (ref: string | number, data: Partial<Pick<Budget, 'status' | 'notes'>>) =>
    api.patch<{ budget: Budget }>(`/management/budgets/${ref}`, data),

  summary: (ref: string | number) =>
    api.get<BudgetSummary>(`/management/budgets/${ref}/summary`),

  lines: (ref: string | number) =>
    api.get<{ lines: BudgetLine[] }>(`/management/budgets/${ref}/lines`),
  createLine: (ref: string | number, data: Partial<BudgetLine>) =>
    api.post<{ line: BudgetLine }>(`/management/budgets/${ref}/lines`, data),
  updateLine: (ref: string | number, id: string, data: Partial<BudgetLine>) =>
    api.patch<{ line: BudgetLine }>(`/management/budgets/${ref}/lines/${id}`, data),
  removeLine: (ref: string | number, id: string) =>
    api.delete<{ deleted: boolean }>(`/management/budgets/${ref}/lines/${id}`),

  entries: (
    ref: string | number,
    params?: {
      status?: BudgetEntryStatus;
      kind?: BudgetLineKind;
      budgetLineId?: string;
      search?: string;
      from?: string;
      to?: string;
      page?: number;
      limit?: number;
    }
  ) =>
    api.get<{
      entries: BudgetEntry[];
      total: number;
      page: number;
      limit: number;
      pages: number;
    }>(`/management/budgets/${ref}/entries`, { params }),
  createEntry: (ref: string | number, data: Partial<BudgetEntry>) =>
    api.post<{ entry: BudgetEntry }>(`/management/budgets/${ref}/entries`, data),
  updateEntry: (ref: string | number, id: string, data: Partial<BudgetEntry>) =>
    api.patch<{ entry: BudgetEntry }>(`/management/budgets/${ref}/entries/${id}`, data),
  removeEntry: (ref: string | number, id: string) =>
    api.delete<{ deleted: boolean }>(`/management/budgets/${ref}/entries/${id}`),
};

// ============================================================
// Management — Cuotas (MemberFee / MemberFeeStatus / FeeCampaign)
// ============================================================

export type FeePeriod = 'monthly' | 'quarterly' | 'annual';
export type MemberFeeStatusValue = 'pending' | 'paid' | 'exempt';
export type FeeCampaignStatus = 'draft' | 'queued' | 'sent' | 'failed';

export interface MemberFee {
  _id: string;
  organization_id: string;
  label: string;
  amount: number; // centimos
  period: FeePeriod;
  yearScope?: number;
  active: boolean;
  budgetLineId?: string | null;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface MemberFeeStatusRow {
  _id: string;
  organization_id: string;
  memberFeeId: string;
  memberId: string;
  period: string;
  amount: number;
  status: MemberFeeStatusValue;
  paidAt?: string;
  budgetEntryId?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface FeeCampaign {
  _id: string;
  organization_id: string;
  label: string;
  memberFeeId?: string | null;
  period?: string;
  recipientIds: string[];
  template?: string;
  status: FeeCampaignStatus;
  sentAt?: string;
  sentCount: number;
  failedCount: number;
  notes?: string;
  createdBy?: string;
  created_at: string;
  updated_at: string;
}

export const feesApi = {
  // Planes
  listPlans: (params?: { active?: boolean; yearScope?: number; period?: FeePeriod }) =>
    api.get<{ fees: MemberFee[] }>('/management/fees', { params }),
  createPlan: (data: Partial<MemberFee>) =>
    api.post<{ fee: MemberFee }>('/management/fees', data),
  updatePlan: (id: string, data: Partial<MemberFee>) =>
    api.patch<{ fee: MemberFee }>(`/management/fees/${id}`, data),
  removePlan: (id: string) =>
    api.delete<{ deleted: boolean }>(`/management/fees/${id}`),

  // Cobros
  listStatuses: (params?: {
    status?: MemberFeeStatusValue;
    memberFeeId?: string;
    memberId?: string;
    period?: string;
    page?: number;
    limit?: number;
  }) =>
    api.get<{
      statuses: MemberFeeStatusRow[];
      total: number;
      page: number;
      limit: number;
      pages: number;
    }>('/management/fee-statuses', { params }),
  createStatus: (data: {
    memberFeeId: string;
    memberId: string;
    period: string;
    amount?: number;
    status?: MemberFeeStatusValue;
    notes?: string;
  }) =>
    api.post<{ feeStatus: MemberFeeStatusRow }>('/management/fee-statuses', data),
  updateStatus: (
    id: string,
    data: { status?: MemberFeeStatusValue; amount?: number; notes?: string }
  ) =>
    api.patch<{ feeStatus: MemberFeeStatusRow }>(`/management/fee-statuses/${id}`, data),
  removeStatus: (id: string) =>
    api.delete<{ deleted: boolean }>(`/management/fee-statuses/${id}`),
  bulkAssign: (data: {
    memberFeeId: string;
    period: string;
    memberIds: string[];
    amount?: number;
  }) =>
    api.post<{ inserted: number; skipped: number; total: number }>(
      '/management/fee-statuses/bulk-assign',
      data
    ),

  // Campanas
  listCampaigns: () =>
    api.get<{ campaigns: FeeCampaign[] }>('/management/fee-campaigns'),
  createCampaign: (data: Partial<FeeCampaign>) =>
    api.post<{ campaign: FeeCampaign }>('/management/fee-campaigns', data),
  updateCampaign: (id: string, data: Partial<FeeCampaign>) =>
    api.patch<{ campaign: FeeCampaign }>(`/management/fee-campaigns/${id}`, data),
  removeCampaign: (id: string) =>
    api.delete<{ deleted: boolean }>(`/management/fee-campaigns/${id}`),
  sendCampaign: (id: string) =>
    api.post<{
      campaign: FeeCampaign;
      sent: number;
      failed: number;
      total: number;
      failedEmails: string[];
    }>(`/management/fee-campaigns/${id}/send`),
};

export const membersApi = {
  list: (params?: {
    status?: MemberStatus;
    search?: string;
    sortBy?: 'fullName' | 'joinedAt' | 'created_at';
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }) =>
    api.get<{
      members: Member[];
      total: number;
      page: number;
      limit: number;
      pages: number;
    }>('/management/members', { params }),

  stats: () => api.get<MemberStats>('/management/members/stats'),

  get: (id: string) => api.get<{ member: Member }>(`/management/members/${id}`),

  create: (data: Partial<Member>) =>
    api.post<{ member: Member }>('/management/members', data),

  update: (id: string, data: Partial<Member>) =>
    api.patch<{ member: Member }>(`/management/members/${id}`, data),

  remove: (id: string) =>
    api.delete<{ deleted: boolean }>(`/management/members/${id}`),
};

export default api;
